import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '../components/seo/SEOHead';
import { api } from '../lib/api';
import { PricingTable } from '../components/service/PricingTable';
import { BenefitsList, EligibilityList } from '../components/service/BenefitsList';
import { DocumentChecklist } from '../components/service/DocumentChecklist';
import { ProcessSteps } from '../components/service/ProcessSteps';
import { FAQAccordion } from '../components/home/FAQAccordion';
import { RelatedServices } from '../components/service/RelatedServices';
import { ConsultCTA } from '../components/service/ConsultCTA';
import { Spinner } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { buildFaqSchema, buildLocalBusinessSchema, buildBreadcrumbSchema } from '../lib/seo-utils';
import type { ServiceLocation } from '@bharat-consultancy/shared';

export default function LocationLanding() {
  const { urlSlug } = useParams<{ urlSlug: string }>();
  const [serviceLocation, setServiceLocation] = useState<ServiceLocation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!urlSlug) return;
    setIsLoading(true);
    setNotFound(false);
    api.locations
      .byUrlSlug(urlSlug)
      .then((res) => setServiceLocation(res.serviceLocation))
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false));
  }, [urlSlug]);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner className="h-8 w-8 text-seal-blue" />
      </div>
    );
  }

  if (notFound || !serviceLocation || !serviceLocation.service || !serviceLocation.location) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-ink">Page Not Found</h1>
        <Link to="/services" className="mt-6 inline-block">
          <Button>Browse All Services</Button>
        </Link>
      </div>
    );
  }

  const { service, location } = serviceLocation;

  // handleSelectPlan / booking navigation intentionally removed here —
  // see lib/bookingFlags.ts.

  return (
    <>
      <SEOHead
        title={serviceLocation.seoTitle ?? `${service.title} in ${location.name} — Bharat Consultancy`}
        description={
          serviceLocation.seoDescription ??
          `Get ${service.title} in ${location.name} with Bharat Consultancy. Fast, reliable, transparent pricing.`
        }
        canonicalPath={`/${serviceLocation.urlSlug}`}
        jsonLd={[
          buildLocalBusinessSchema({
            name: 'Bharat Consultancy',
            address: '2nd Floor, Aradhya Complex, SN Sarkar St, Munsifdanga, Purulia, West Bengal 723101',
            phone: '+918170896296',
            url: 'https://bharat-consultancy.com',
            areaServed: [location.name],
          }),
          buildBreadcrumbSchema([
            { name: 'Home', url: 'https://bharat-consultancy.com/' },
            { name: service.title, url: `https://bharat-consultancy.com/services/${service.slug}` },
            { name: location.name, url: `https://bharat-consultancy.com/${serviceLocation.urlSlug}` },
          ]),
          ...(service.faqs?.length ? [buildFaqSchema(service.faqs)] : []),
        ]}
      />

      <section className="border-b border-line bg-white py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <p className="font-mono text-xs uppercase tracking-widest text-ledger-green">{location.name}, {location.state}</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-ink sm:text-4xl">
            {service.title} in {location.name}
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-ink/65">
            {serviceLocation.customIntro ??
              `Looking for reliable ${service.title.toLowerCase()} in ${location.name}? Bharat Consultancy provides fast, transparent, and locally-trusted service for businesses and individuals across ${location.name} and ${location.state}.`}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl space-y-14 px-4 py-12 sm:px-6">
        {/* Overview */}
        {service.fullContent && (
          <section>
            <h2 className="font-display text-2xl font-bold text-ink">
              Why Choose Bharat Consultancy in {location.name}
            </h2>
            <p className="mt-4 leading-relaxed text-ink/75">{service.fullContent}</p>
          </section>
        )}

        {/* Benefits */}
        <section>
          <h2 className="font-display text-2xl font-bold text-ink">Benefits</h2>
          <div className="mt-4">
            <BenefitsList
              content={
                service.benefits ??
                `- Expert handling of all paperwork\n- Transparent, upfront pricing\n- Fast turnaround time\n- Dedicated support throughout the process`
              }
            />
          </div>
        </section>

        {/* Eligibility */}
        <section>
          <h2 className="font-display text-2xl font-bold text-ink">Eligibility</h2>
          <div className="mt-4">
            <EligibilityList
              content={
                service.eligibility ??
                `- Valid for individuals and businesses in ${location.state}\n- Required documents must be valid and up to date`
              }
            />
          </div>
        </section>

        {service.requiredDocuments && service.requiredDocuments.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-ink">Required Documents</h2>
            <div className="mt-4">
              <DocumentChecklist documents={service.requiredDocuments} />
            </div>
          </section>
        )}

        {service.processSteps && service.processSteps.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-ink">How It Works</h2>
            <div className="mt-6">
              <ProcessSteps steps={service.processSteps} />
            </div>
          </section>
        )}

        {service.plans && service.plans.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-ink">Plans</h2>
            <div className="mt-6">
              <PricingTable plans={service.plans} />
            </div>
            <div className="mt-6">
              <ConsultCTA serviceId={service.id} serviceName={service.title} />
            </div>
          </section>
        )}

        {service.faqs && service.faqs.length > 0 && (
          <div className="-mx-4 sm:-mx-6">
            <FAQAccordion faqs={service.faqs} title={`${service.title} in ${location.name} — FAQs`} />
          </div>
        )}

        {/* Why Choose Us */}
        <section className="rounded-lg bg-seal-blue/5 p-6 text-center">
          <h2 className="font-display text-xl font-bold text-ink">Why Choose Bharat Consultancy</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-ink/65">
            Local expertise in {location.name}, transparent pricing, and a dedicated team that keeps you updated at
            every step.
          </p>
        </section>

        {/* Related Services */}
        {service.relatedServices && service.relatedServices.length > 0 && (
          <RelatedServices services={service.relatedServices} />
        )}

        {/* Contact CTA */}
        <section className="flex flex-col items-center gap-4 rounded-lg border border-line bg-white p-8 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <h2 className="font-display text-xl font-bold text-ink">Serving {location.name} and nearby areas</h2>
            <p className="mt-1 text-sm text-ink/60">Call or WhatsApp us to get started today.</p>
          </div>
          <div className="flex gap-3">
            <a href="tel:+918170896296">
              <Button variant="outline">Call 81708 96296</Button>
            </a>
            <Link to={`/consult?serviceId=${service.id}&serviceName=${encodeURIComponent(service.title)}`}>
              <Button variant="accent">Book a 30-Min Session</Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
